import React, { useCallback, useState, createContext, useContext } from 'react';
type Lang = 'en' | 'es';
interface LanguageContextType {
  lang: Lang;
  toggleLang: () => void;
  t: (key: string) => string;
}
const translations: Record<string, Record<Lang, string>> = {
  // Navbar
  'nav.team': {
    en: 'Team',
    es: 'Equipo'
  },
  'nav.matches': {
    en: 'Matches',
    es: 'Partidos'
  },
  'nav.deals': {
    en: 'Deals',
    es: 'Ofertas'
  },
  'nav.liveScores': {
    en: 'Live Scores',
    es: 'Resultados en Vivo'
  },
  'nav.orderYours': {
    en: 'Order Yours',
    es: 'Pide la Tuya'
  },
  // Hero
  'hero.badge': {
    en: "You're now part of England 2026",
    es: 'Ahora eres parte de Inglaterra 2026'
  },
  'hero.title1': {
    en: "Don't Just Support England,",
    es: 'No Solo Apoyes a Inglaterra,'
  },
  'hero.title2': {
    en: 'Live It',
    es: 'Vívelo'
  },
  'hero.subtitle': {
    en: 'Updated in real live time. Access exclusive content, match updates, and travel deals directly from your digital hub.',
    es: 'Actualizado en tiempo real. Accede a contenido exclusivo, actualizaciones de partidos y ofertas de viaje directamente desde tu hub digital.'
  },
  'hero.cta1': {
    en: 'Watch Matches',
    es: 'Ver Partidos'
  },
  'hero.cta2': {
    en: 'Explore Team',
    es: 'Explorar Equipo'
  },
  'hero.scroll': {
    en: 'Scroll to discover',
    es: 'Desliza para descubrir'
  },
  // Team Overview
  'team.label': {
    en: 'Our Legacy',
    es: 'Nuestro Legado'
  },
  'team.title1': {
    en: 'A Nation Forged',
    es: 'Una Nación Forjada'
  },
  'team.title2': {
    en: 'In Glory',
    es: 'En la Gloria'
  },
  'team.desc': {
    en: 'The England national team represents more than just football; it embodies the passion, resilience, and history of a nation. From the historic triumph in 1966 to reaching the Euro 2020 final, the Three Lions continue to inspire millions.',
    es: 'La selección de Inglaterra representa más que solo fútbol; encarna la pasión, la resiliencia y la historia de una nación. Desde el histórico triunfo en 1966 hasta llegar a la final de la Euro 2020, los Tres Leones continúan inspirando a millones.'
  },
  'team.since': {
    en: 'Since 1872',
    es: 'Desde 1872'
  },
  'team.history': {
    en: 'Explore Our Full History →',
    es: 'Explora Nuestra Historia Completa →'
  },
  'team.euro': {
    en: 'World Cup 1966',
    es: 'Mundial 1966'
  },
  'team.euroDesc': {
    en: 'Champions of the World',
    es: 'Campeones del Mundo'
  },
  'team.nationsLeague': {
    en: 'Euro 2020',
    es: 'Euro 2020'
  },
  'team.nationsLeagueDesc': {
    en: 'Runners-up',
    es: 'Subcampeones'
  },
  'team.euros': {
    en: '11 Euros',
    es: '11 Eurocopas'
  },
  'team.eurosDesc': {
    en: 'Championship Appearances',
    es: 'Participaciones en Campeonatos'
  },
  'team.worldCups': {
    en: '16 World Cups',
    es: '16 Mundiales'
  },
  'team.worldCupsDesc': {
    en: 'Global Stage Appearances',
    es: 'Participaciones en el Escenario Global'
  },
  'team.videoLabel': {
    en: 'Three Lions',
    es: 'Tres Leones'
  },
  'team.videoTitle1': {
    en: 'One Nation, One',
    es: 'Una Nación, Un'
  },
  'team.videoTitle2': {
    en: 'Heartbeat',
    es: 'Latido'
  },
  // Key Players
  'players.label': {
    en: 'The Squad',
    es: 'La Plantilla'
  },
  'players.title1': {
    en: 'The Stars of',
    es: 'Las Estrellas de'
  },
  'players.title2': {
    en: 'England',
    es: 'Inglaterra'
  },
  'players.viewSquad': {
    en: 'View Full Squad on ESPN.com',
    es: 'Ver Plantilla Completa en ESPN.com'
  },
  'players.forward': {
    en: 'Forward',
    es: 'Delantero'
  },
  'players.midfielder': {
    en: 'Midfielder',
    es: 'Centrocampista'
  },
  'players.defender': {
    en: 'Defender',
    es: 'Defensa'
  },
  // Match Center
  'matches.label': {
    en: 'World Cup 2026',
    es: 'Mundial 2026'
  },
  'matches.title1': {
    en: 'Match',
    es: 'Centro de'
  },
  'matches.title2': {
    en: 'Center',
    es: 'Partidos'
  },
  'matches.desc': {
    en: 'Follow the Three Lions in Group B. Get live updates, official lineups, and real-time scores from the biggest stage in football.',
    es: 'Sigue a los Tres Leones en el Grupo B. Obtén actualizaciones en vivo, alineaciones oficiales y resultados en tiempo real del mayor escenario del fútbol.'
  },
  'matches.standings': {
    en: 'Group Standings',
    es: 'Clasificación del Grupo'
  },
  'matches.live': {
    en: 'Live',
    es: 'En Vivo'
  },
  'matches.autoRefresh': {
    en: 'Auto-refreshes every 5 min',
    es: 'Se actualiza automáticamente cada 5 min'
  },
  'matches.refreshNow': {
    en: 'Refresh Now',
    es: 'Actualizar Ahora'
  },
  'matches.team': {
    en: 'Team',
    es: 'Equipo'
  },
  'matches.viewStandings': {
    en: 'View Live Standings on FlashScore',
    es: 'Ver Clasificación en Vivo en FlashScore'
  },
  'matches.nextMatch': {
    en: 'Next Match',
    es: 'Próximo Partido'
  },
  'matches.espn': {
    en: 'ESPN Schedule',
    es: 'Calendario ESPN'
  },
  'matches.sofascore': {
    en: 'SofaScore',
    es: 'SofaScore'
  },
  'matches.flashscore': {
    en: 'FlashScore Live',
    es: 'FlashScore en Vivo'
  },
  'matches.foxsports': {
    en: 'Fox Sports',
    es: 'Fox Sports'
  },
  // Stats
  'stats.label': {
    en: 'England By The Numbers',
    es: 'Inglaterra en Números'
  },
  'stats.worldCup': {
    en: 'World Cup Appearances',
    es: 'Participaciones en Mundiales'
  },
  'stats.titles': {
    en: 'Major Titles',
    es: 'Títulos Principales'
  },
  'stats.goals': {
    en: 'International Goals',
    es: 'Goles Internacionales'
  },
  'stats.ronaldo': {
    en: 'Kane Goals (Record)',
    es: 'Goles de Kane (Récord)'
  },
  // Historic Moments
  'history.label': {
    en: 'The Archives',
    es: 'Los Archivos'
  },
  'history.title1': {
    en: 'Moments That',
    es: 'Momentos Que'
  },
  'history.title2': {
    en: 'Made History',
    es: 'Hicieron Historia'
  },
  'history.wc1966': {
    en: '1966 World Cup Final',
    es: 'Final del Mundial 1966'
  },
  'history.beckham': {
    en: 'Beckham vs Greece 2001',
    es: 'Beckham vs Grecia 2001'
  },
  'history.gazza': {
    en: 'Gascoigne vs Scotland Euro 96',
    es: 'Gascoigne vs Escocia Euro 96'
  },
  'history.munich': {
    en: '5-1 vs Germany in Munich 2001',
    es: '5-1 vs Alemania en Múnich 2001'
  },
  'history.euro2020': {
    en: 'Euro 2020 - The Journey',
    es: 'Euro 2020 - El Viaje'
  },
  'history.lineker': {
    en: 'Lineker at Italia 90',
    es: 'Lineker en Italia 90'
  },
  'history.bellingham': {
    en: 'Bellingham Era',
    es: 'La Era de Bellingham'
  },
  'history.rooney': {
    en: 'Rooney - All-Time Record',
    es: 'Rooney - Récord Histórico'
  },
  // Fan Experience
  'fan.label': {
    en: 'More Than A Game',
    es: 'Más Que Un Juego'
  },
  'fan.title1': {
    en: 'Feel the Passion of',
    es: 'Siente la Pasión de'
  },
  'fan.title2': {
    en: 'England',
    es: 'Inglaterra'
  },
  'fan.desc': {
    en: 'Join millions of voices singing as one. Wear the colors, feel the history, and be part of the journey to global glory.',
    es: 'Únete a millones de voces cantando como una sola. Viste los colores, siente la historia y sé parte del viaje hacia la gloria mundial.'
  },
  // Tickets / Travel Deals
  'deals.label': {
    en: 'Travel to the World Cup',
    es: 'Viaja al Mundial'
  },
  'deals.title1': {
    en: 'Exclusive',
    es: 'Ofertas Exclusivas'
  },
  'deals.title2': {
    en: 'Fan Deals',
    es: 'para Fans'
  },
  'deals.desc': {
    en: 'Exclusive discounts for England fans on tickets, hotels, flights & transport to support the Three Lions in North America.',
    es: 'Descuentos exclusivos para fans de Inglaterra en entradas, hoteles, vuelos y transporte para apoyar a los Tres Leones en Norteamérica.'
  },
  'deals.unlockVip': {
    en: 'Unlock VIP Discounts',
    es: 'Desbloquea Descuentos VIP'
  },
  'deals.applyCode': {
    en: 'Apply code at checkout on partner sites',
    es: 'Aplica el código en el checkout de sitios asociados'
  },
  'deals.tickets': {
    en: 'Match Tickets',
    es: 'Entradas para Partidos'
  },
  'deals.ticketsDesc': {
    en: 'Up to 15% off group stage packages',
    es: 'Hasta 15% de descuento en paquetes de fase de grupos'
  },
  'deals.findTickets': {
    en: 'Find Tickets',
    es: 'Buscar Entradas'
  },
  'deals.hotels': {
    en: 'Hotels & Accommodation',
    es: 'Hoteles y Alojamiento'
  },
  'deals.hotelsDesc': {
    en: 'Partner hotel deals near match venues',
    es: 'Ofertas de hoteles asociados cerca de los estadios'
  },
  'deals.bookHotels': {
    en: 'Book Hotels',
    es: 'Reservar Hoteles'
  },
  'deals.flights': {
    en: 'Flights',
    es: 'Vuelos'
  },
  'deals.flightsDesc': {
    en: 'Special fares from London & Manchester to USA',
    es: 'Tarifas especiales desde Londres y Manchester a EE. UU.'
  },
  'deals.searchFlights': {
    en: 'Search Flights',
    es: 'Buscar Vuelos'
  },
  'deals.transport': {
    en: 'Local Transport',
    es: 'Transporte Local'
  },
  'deals.transportDesc': {
    en: 'Shuttle services & car rentals at match cities',
    es: 'Servicios de transporte y alquiler de coches en ciudades de partidos'
  },
  'deals.planTransport': {
    en: 'Plan Transport',
    es: 'Planificar Transporte'
  },
  // Fan HQ / Gallery
  'fanHq.label': {
    en: 'Everything You Need for England 2026',
    es: 'Todo lo que Necesitas para Inglaterra 2026'
  },
  'fanHq.title1': {
    en: 'Fan',
    es: 'Sede del'
  },
  'fanHq.title2': {
    en: 'HQ',
    es: 'Fan'
  },
  'fanHq.countdown': {
    en: "Countdown to England's First Match",
    es: 'Cuenta Regresiva para el Primer Partido de Inglaterra'
  },
  'fanHq.days': {
    en: 'Days',
    es: 'Días'
  },
  'fanHq.hrs': {
    en: 'Hrs',
    es: 'Hrs'
  },
  'fanHq.min': {
    en: 'Min',
    es: 'Min'
  },
  'fanHq.sec': {
    en: 'Sec',
    es: 'Seg'
  },
  'fanHq.capacity': {
    en: 'Capacity',
    es: 'Capacidad'
  },
  'fanHq.chants': {
    en: 'Learn the',
    es: 'Aprende los'
  },
  'fanHq.chantsHighlight': {
    en: 'Chants',
    es: 'Cánticos'
  },
  'fanHq.nationalAnthem': {
    en: 'National Anthem',
    es: 'Himno Nacional'
  },
  'fanHq.classicChant': {
    en: 'Classic Stadium Chant',
    es: 'Cántico Clásico de Estadio'
  },
  'fanHq.crowdFavorite': {
    en: 'Crowd Favorite',
    es: 'Favorito del Público'
  },
  'fanHq.victoryChant': {
    en: 'Victory Chant',
    es: 'Cántico de Victoria'
  },
  'fanHq.watchYoutube': {
    en: 'Watch on YouTube',
    es: 'Ver en YouTube'
  },
  'fanHq.phrases': {
    en: 'Essential',
    es: 'Frases'
  },
  'fanHq.phrasesHighlight': {
    en: 'Phrases',
    es: 'Esenciales'
  },
  'fanHq.quickLinks': {
    en: 'Quick',
    es: 'Enlaces'
  },
  'fanHq.quickLinksHighlight': {
    en: 'Links',
    es: 'Rápidos'
  },
  'fanHq.whereToWatch': {
    en: 'Where to Watch',
    es: 'Dónde Ver'
  },
  'fanHq.whereToWatchDesc': {
    en: 'Streaming & TV channels',
    es: 'Canales de TV y streaming'
  },
  'fanHq.fanZones': {
    en: 'Fan Info Channel',
    es: 'Canal de Info para Fans'
  },
  'fanHq.fanZonesDesc': {
    en: 'Official FIFA fan information',
    es: 'Información oficial de FIFA para fans'
  },
  'fanHq.fifaRanking': {
    en: 'FIFA Ranking',
    es: 'Ranking FIFA'
  },
  'fanHq.fifaRankingDesc': {
    en: 'Official world rankings',
    es: 'Rankings mundiales oficiales'
  },
  'fanHq.fullSchedule': {
    en: 'Full WC Schedule',
    es: 'Calendario Completo del Mundial'
  },
  'fanHq.fullScheduleDesc': {
    en: 'All 104 matches',
    es: 'Los 104 partidos'
  },
  // Footer
  'footer.desc': {
    en: 'An official Qtap by STDI digital product. Your smart NFC T-shirt hub for England 2026 — real-time match data, exclusive content, and travel deals at your fingertips.',
    es: 'Un producto digital oficial de Qtap by STDI. Tu hub de camiseta NFC inteligente para Inglaterra 2026 — datos de partidos en tiempo real, contenido exclusivo y ofertas de viaje a tu alcance.'
  },
  'footer.resources': {
    en: 'Resources',
    es: 'Recursos'
  },
  'footer.team': {
    en: 'Team',
    es: 'Equipo'
  },
  'footer.firstTeam': {
    en: 'First Team',
    es: 'Primer Equipo'
  },
  'footer.matches': {
    en: 'Matches',
    es: 'Partidos'
  },
  'footer.standings': {
    en: 'Standings',
    es: 'Clasificación'
  },
  'footer.travelDeals': {
    en: 'Travel Deals',
    es: 'Ofertas de Viaje'
  },
  'footer.support': {
    en: 'Support',
    es: 'Soporte'
  },
  'footer.contactUs': {
    en: 'Contact Us',
    es: 'Contáctanos'
  },
  'footer.faq': {
    en: 'FAQ',
    es: 'Preguntas Frecuentes'
  },
  'footer.privacy': {
    en: 'Privacy Policy',
    es: 'Política de Privacidad'
  },
  'footer.terms': {
    en: 'Terms of Service',
    es: 'Términos de Servicio'
  },
  'footer.cookies': {
    en: 'Cookie Policy',
    es: 'Política de Cookies'
  },
  'footer.copyright': {
    en: 'Qtap by STDI. All rights reserved.',
    es: 'Qtap by STDI. Todos los derechos reservados.'
  }
};
const LanguageContext = createContext<LanguageContextType>({
  lang: 'en',
  toggleLang: () => {},
  t: (key: string) => key
});
export function LanguageProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [lang, setLang] = useState<Lang>('en');
  const toggleLang = useCallback(() => {
    setLang(prev => prev === 'en' ? 'es' : 'en');
  }, []);
  const t = useCallback((key: string) => {
    return translations[key]?.[lang] || key;
  }, [lang]);
  return <LanguageContext.Provider value={{
    lang,
    toggleLang,
    t
  }} data-id="element-211">
      {children}
    </LanguageContext.Provider>;
}
export function useLanguage() {
  return useContext(LanguageContext);
}